import { sql } from "drizzle-orm";
import {
	index,
	jsonb,
	numeric,
	pgEnum,
	pgTable,
	text,
	timestamp,
	unique,
	uniqueIndex,
	uuid
} from "drizzle-orm/pg-core";

import { baseTableProps } from "../../baseTableProps";
import { displayNameProps } from "../_shared/display-name";
import { addresses } from "../addresses/addresses.model";
import { companies } from "../companies/companies.model";

export const cadPropertyTypeEnum = pgEnum("cad_property_type", ["real", "personal", "mobile_home"]);
export const cadParcelStatusEnum = pgEnum("cad_parcel_status", ["active", "archived"]);

export type CadLandRow = {
	type: string;
	description: string;
	acreage: number | null;
	sqft: number | null;
	effFront: number | null;
	effDepth: number | null;
	marketValue: number | null;
	prodValue: number | null;
};

export type CadSourceSnapshot =
	| {
			schemaVersion: 1;
			provider: "bis-consultants";
			capturedAt: string;
			identity: { registryCountyKey: string; canonicalKey: string; aliases: string[] };
			payload: {
				propertyDetails: Record<string, unknown>;
				rollValueHistory: Array<Record<string, unknown>>;
				taxingJurisdictions: Record<string, unknown>;
				improvements: Record<string, unknown>;
				land: Array<Record<string, unknown>>;
				deedHistory: Array<Record<string, unknown>>;
			};
	  }
	| {
			schemaVersion: 1;
			provider: "tyler-tech";
			capturedAt: string;
			identity: { registryCountyKey: string; canonicalKey: string; aliases: string[] };
			payload: {
				matchedProperty: Record<string, unknown> & {
					PropertyQuickRefID: string;
					PropertyNumber?: string | null;
				};
				latestRollYear?: number;
			};
	  };

export const cadParcels = pgTable(
	"cad_parcels",
	{
		...baseTableProps,
		parcelId: text("parcel_id").notNull(),
		county: text("county").notNull(),
		registryCountyKey: text("registry_county_key"),
		provider: text("provider").$type<"bis-consultants" | "tyler-tech">(),
		providerKey: text("provider_key"),
		sourceSnapshot: jsonb("source_snapshot").$type<CadSourceSnapshot>(),
		propertyType: cadPropertyTypeEnum("property_type"),
		status: cadParcelStatusEnum("status").notNull().default("active"),
		geoId: text("geo_id"),
		situsAddress: text("situs_address"),
		legalDescription: text("legal_description"),
		landSquareFeet: numeric("land_square_feet", {
			precision: 14,
			scale: 2,
			mode: "number"
		}),
		landAcreage: numeric("land_acreage", { precision: 12, scale: 4, mode: "number" }),
		landMarketValue: numeric("land_market_value", {
			precision: 14,
			scale: 2,
			mode: "number"
		}),
		landDetail: jsonb("land_detail").$type<CadLandRow[]>(),
		landScrapedAt: timestamp("land_scraped_at", { withTimezone: true, mode: "string" }),
		cadOwnerId: text("cad_owner_id"),
		cadOwnerName: text("cad_owner_name"),
		companyId: uuid("company_id").references(() => companies.id),
		...displayNameProps
	},
	(table) => [
		unique("cad_parcels_county_parcel_uniq").on(table.county, table.parcelId),
		uniqueIndex("cad_parcels_provider_identity_uniq")
			.on(table.registryCountyKey, table.provider, table.providerKey)
			.where(
				sql`${table.registryCountyKey} IS NOT NULL AND ${table.provider} IS NOT NULL AND ${table.providerKey} IS NOT NULL`
			),
		index("cad_parcels_parcel_id_idx").on(table.parcelId),
		index("cad_parcels_company_idx").on(table.companyId)
	]
);

export const xCadParcelAddresses = pgTable(
	"x_cad_parcel_addresses",
	{
		...baseTableProps,
		cadParcelId: uuid("cad_parcel_id")
			.references(() => cadParcels.id, { onDelete: "cascade" })
			.notNull(),
		addressId: uuid("address_id")
			.references(() => addresses.id, { onDelete: "cascade" })
			.notNull()
	},
	(table) => [
		index("x_cad_parcel_addresses_cad_parcel_id_idx").on(table.cadParcelId),
		index("x_cad_parcel_addresses_address_id_idx").on(table.addressId)
	]
);
